import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { 
  LayoutDashboard, BookOpen, Target, MapPin, BarChart3, 
  FileText, LogOut, Menu, X, Crown, ChevronDown, ChevronUp, Layers,
  Trophy, Zap, AlertTriangle, Calendar, User, CreditCard,
  GraduationCap, Clock, BookMarked, Mail, Settings, Shield, LineChart
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DashboardLayout = ({ children, auth }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();
  
  // Admin email for fallback check
  const ADMIN_EMAIL = "mail@hemantsahni.com";

  // Check if user is admin
  useEffect(() => {
    // Immediate check based on email (fallback)
    if (auth.user?.email === ADMIN_EMAIL) {
      setIsAdmin(true);
    }
    
    const checkAdminStatus = async () => {
      const storedToken = localStorage.getItem("token") || auth.token;
      if (!storedToken) {
        // Still check email fallback
        if (auth.user?.email === ADMIN_EMAIL) {
          setIsAdmin(true);
        }
        return;
      }
      
      try {
        const response = await axios.get(`${API}/user/is-admin`, {
          headers: { Authorization: `Bearer ${storedToken}` },
          withCredentials: true
        });
        setIsAdmin(response.data.is_admin);
      } catch (error) {
        console.error("Admin check failed:", error);
        // Fallback to email check
        if (auth.user?.email === ADMIN_EMAIL) {
          setIsAdmin(true);
        }
      }
    };
    
    if (auth.user) {
      checkAdminStatus();
    }
  }, [auth.user, auth.token]);

  // Fetch saved study plan for journey card
  const [savedPlan, setSavedPlan] = useState(null);
  useEffect(() => {
    const fetchPlan = async () => {
      const storedToken = localStorage.getItem("token") || auth.token;
      if (!storedToken) return;
      try {
        const response = await axios.get(`${API}/study-plan/saved`, {
          headers: { Authorization: `Bearer ${storedToken}` },
          withCredentials: true
        });
        if (response.data.plan) {
          setSavedPlan(response.data.plan);
        }
      } catch (e) {
        // Silent fail
      }
    };
    if (auth.user) fetchPlan();
  }, [auth.user, auth.token]);

  // Organized navigation sections — student journey flow
  const navigationSections = [
    {
      title: null,
      items: [
        { name: t("nav.dashboard") || "Dashboard", href: "/dashboard", icon: LayoutDashboard },
      ]
    },
    {
      title: t("nav.learn") || "Learn",
      items: [
        { name: t("nav.discoverCanada") || "Discover Canada", href: "/discover-canada", icon: BookMarked },
        { name: t("nav.studyGuide") || "Study Guide", href: "/study", icon: BookOpen },
        { name: t("nav.flashcards") || "Flashcards", href: "/flashcards", icon: Layers },
      ]
    },
    {
      title: t("nav.practice") || "Practice",
      items: [
        { name: t("nav.chapterPractice") || "Chapter Practice", href: "/chapter-practice", icon: Target },
        { name: t("nav.simulations") || "Simulations", href: "/simulations", icon: Zap },
      ]
    },
    {
      title: "Test Ready",
      items: [
        { name: t("nav.mockTests") || "Mock Tests", href: "/mock-tests", icon: Trophy },
        { name: t("nav.progressSummary") || "Progress", href: "/my-progress", icon: LineChart },
        { name: t("nav.analytics") || "Analytics", href: "/analytics", icon: BarChart3 },
        { name: t("nav.weakAreas") || "Weak Areas", href: "/weak-areas", icon: AlertTriangle },
      ]
    },
  ];

  const handleLogout = async () => {
    await auth.logout();
    window.location.href = "/";
  };

  const isActive = (href) => location.pathname === href;

  const tierBadge = {
    free: { label: "Free", color: "bg-slate-100 text-slate-600" },
    pro: { label: "Pro", color: "bg-blue-100 text-blue-600" },
    premium: { label: "Premium", color: "bg-yellow-100 text-yellow-700" }
  };

  const currentTier = tierBadge[auth.user?.subscription_tier || "free"];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-9 h-9 bg-maple rounded-lg flex items-center justify-center">
                <span className="text-white font-heading font-bold text-lg">C</span>
              </div>
              <div className="flex flex-col">
                <span className="font-heading font-bold text-base text-slate-900 leading-tight">CanadaTestPrep</span>
                <span className="text-[9px] text-slate-400 leading-tight">powered by MaplePass</span>
              </div>
            </Link>
            <button 
              className="lg:hidden p-2 text-slate-500 hover:text-slate-700"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 overflow-y-auto">
            {navigationSections.map((section, sectionIndex) => (
              <div key={sectionIndex} className={sectionIndex > 0 ? "mt-6" : ""}>
                {section.title && (
                  <div className="px-3 mb-2">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      {section.title}
                    </span>
                  </div>
                )}
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.href);
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`
                          flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                          transition-colors duration-150
                          ${active 
                            ? 'bg-maple-50 text-maple border-l-4 border-maple -ml-px pl-[11px]' 
                            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}
                        `}
                        data-testid={`nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
                      >
                        <Icon className="w-5 h-5" />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Journey Card — only shown when a study plan exists */}
          {savedPlan && (() => {
            const testDate = new Date(savedPlan.test_date);
            const now = new Date();
            const daysLeft = Math.max(0, Math.ceil((testDate - now) / (1000 * 60 * 60 * 24)));
            const totalDays = savedPlan.total_days || savedPlan.days_until_test;
            const daysPassed = Math.max(0, totalDays - daysLeft);
            const progress = Math.min(100, Math.round((daysPassed / totalDays) * 100));
            return (
              <div className="mx-3 mb-3">
                <Link to="/journey" className="block p-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-maple" />
                    <span className="text-sm font-semibold text-slate-800">My Study Plan</span>
                  </div>
                  <div className="text-xs text-slate-500 mb-2">
                    Test: {testDate.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })} &middot; Day {daysPassed} of {totalDays}
                  </div>
                  <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-maple rounded-full transition-all" 
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </Link>
              </div>
            );
          })()}

          {/* Upgrade CTA */}
          {auth.user?.subscription_tier === "free" && (
            <div className="p-4 mx-3 mb-3 bg-gradient-to-br from-maple-50 to-maple-100 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-5 h-5 text-maple" />
                <span className="font-heading font-semibold text-slate-900">{t("common.upgrade") || "Upgrade"}</span>
              </div>
              <p className="text-sm text-slate-600 mb-3">
                {auth.user?.language === "fr" 
                  ? "Obtenez un accès illimité et toutes les fonctionnalités"
                  : "Get unlimited access and all features"
                }
              </p>
              <Link to="/pricing">
                <Button size="sm" className="w-full bg-maple hover:bg-maple-600 text-white">
                  {t("common.viewPlans") || "View Plans"}
                </Button>
              </Link>
            </div>
          )}

          {/* User Section with Account Menu */}
          <div className="border-t border-slate-200 relative">
            {/* Account Menu Popover */}
            {showAccountMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowAccountMenu(false)} />
                <div className="absolute bottom-full left-3 right-3 mb-2 bg-white border border-slate-200 rounded-xl shadow-lg py-2 z-50">
                  <Link
                    to="/settings"
                    className={`flex items-center gap-3 mx-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive("/settings")
                        ? 'bg-maple-50 text-maple'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                    onClick={() => setShowAccountMenu(false)}
                    data-testid="nav-settings"
                  >
                    <Settings className="w-4 h-4" />
                    {t("common.settings") || "Settings"}
                  </Link>
                  <Link
                    to="/pricing"
                    className="flex items-center gap-3 mx-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                    onClick={() => setShowAccountMenu(false)}
                    data-testid="nav-subscription"
                  >
                    <CreditCard className="w-4 h-4" />
                    {t("common.managePlan") || "Manage Plan"}
                  </Link>
                  <Link
                    to="/contact"
                    className="flex items-center gap-3 mx-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                    onClick={() => setShowAccountMenu(false)}
                    data-testid="nav-contact"
                  >
                    <Mail className="w-4 h-4" />
                    {t("common.contactUs") || "Contact Us"}
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className={`flex items-center gap-3 mx-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive("/admin")
                          ? 'bg-maple-50 text-maple'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`}
                      onClick={() => setShowAccountMenu(false)}
                      data-testid="nav-admin"
                    >
                      <Shield className="w-4 h-4" />
                      Admin
                    </Link>
                  )}
                  <div className="border-t border-slate-200 mt-1 pt-1 mx-2">
                    <button
                      onClick={() => { setShowAccountMenu(false); handleLogout(); }}
                      className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                      data-testid="logout-sidebar-btn"
                    >
                      <LogOut className="w-4 h-4" />
                      Log out
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Clickable User Area */}
            <button
              onClick={() => setShowAccountMenu(!showAccountMenu)}
              className="w-full p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left"
              data-testid="user-account-toggle"
            >
              <Avatar className="w-10 h-10">
                <AvatarImage src={auth.user?.picture} />
                <AvatarFallback className="bg-maple text-white">
                  {auth.user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {auth.user?.name || "User"}
                </p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${currentTier.color}`}>
                  {currentTier.label}
                </span>
              </div>
              {showAccountMenu 
                ? <ChevronUp className="w-4 h-4 text-slate-400" />
                : <ChevronDown className="w-4 h-4 text-slate-400" />
              }
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200">
          <div className="flex items-center justify-between h-16 px-4 lg:px-8">
            <button
              className="lg:hidden p-2 text-slate-500 hover:text-slate-700"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex-1" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={auth.user?.picture} />
                    <AvatarFallback className="bg-maple text-white text-sm">
                      {auth.user?.name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:block text-sm font-medium text-slate-700">
                    {auth.user?.name}
                  </span>
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium text-slate-900">{auth.user?.name}</p>
                  <p className="text-xs text-slate-500">{auth.user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/pricing" className="flex items-center gap-2 cursor-pointer">
                    <Crown className="w-4 h-4" />
                    Subscription
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-red-600 focus:text-red-600 cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
